import SearchableSelect from '@components/searchable-select-input';
import type { JarvisFieldSize } from '@components/jarvis-ui/jarvis-field-utils';
import axiosInstance from '@libs/axios';
import type React from 'react';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import _, { isArray } from 'lodash';
import { notifications } from '@mantine/notifications';

type Props = {
  value: string;
  onChange: (val: any, origin?: any) => void;
  error?: string;
  initialSearchValue?: string;
  label?: string;
  /** Matches SearchableSelect's density scale; 'xs' is used by the compact
   *  upload rows. */
  size?: JarvisFieldSize;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  dynamicKeyParam: string;
  path: string;
  additionalParam?: string;
  valueOnResponseKey?: string;
  optionsLabelKeys: string[];
  defaultValuePath?: string;
  id?: string;
  className?: string;
  classNames?: {
    root?: string;
    input?: string;
    error?: string;
  };
  /** Inline overrides for the control, forwarded to SearchableSelect. */
  style?: React.CSSProperties;
  useDefault?: boolean;
  useLocalQuery?: boolean;
  /** When true, the options list fetches immediately on mount (no "lazy on focus"). */
  loadOnMount?: boolean;
  onBlur?: () => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  name?: string;
  data?: any[];
  noCache?: boolean;
  renderOption?: (item: any) => React.ReactNode;
  /** Options offered beside the fetched list, for values the endpoint has no
   *  row for. They sort first and answer the same search text. Pass a stable
   *  (module-level or memoized) array — a fresh literal every render would
   *  defeat the options memo. */
  extraOptions?: ReadonlyArray<{ label: string; value: string }>;
  /** With useLocalQuery: replaces the plain "label contains the text" filter,
   *  so a screen can match loosely and rank its own results. Runs over the
   *  extra options and the fetched ones together. */
  filterOptions?: <T extends { label: unknown }>(options: T[], search: string) => T[];
  /** Shows an "x" in the input that empties the search text. */
  clearable?: boolean;
};

/** Shared empty default, so the prop's identity is stable when unset. */
const NO_EXTRA_OPTIONS: ReadonlyArray<{ label: string; value: string }> = [];

const AsyncSelect = (props: Props) => {
  const {
    onChange,
    error,
    value = '',
    initialSearchValue = '',
    label = '',
    size = 'sm',
    placeholder = 'Pilih MOT',
    disabled = false,
    required = false,
    dynamicKeyParam = 'query',
    additionalParam = '',
    path,
    valueOnResponseKey = 'id',
    optionsLabelKeys = [],
    defaultValuePath,
    id,
    className,
    useDefault = true,
    useLocalQuery = false,
    loadOnMount = false,
    classNames = {
      root: 'transition-all duration-200 hover:scale-[1.01]',
      input: 'transition-colors duration-200',
      error: '',
    },
    onBlur,
    onKeyPress,
    name,
    noCache = false,
    renderOption,
    style,
    extraOptions = NO_EXTRA_OPTIONS,
    filterOptions,
    clearable = false,
  } = props;

  const [isComponentRendered, setIsComponentRendered] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initialSearchValue);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialSearchValue);
  // When true, options query fetches with empty string to populate the full dropdown list
  const [showFullList, setShowFullList] = useState(false);
  // Latches true once the dropdown is focused, so the options list only loads
  // on demand instead of eagerly on mount. loadOnMount forces the eager load.
  const [hasOpened, setHasOpened] = useState(loadOnMount);
  const skipNextSearchRef = useRef(false);
  // Set when the user empties a focused field: if the parent then clears the
  // value, the input remounts (see the key below) and needs its focus back.
  const refocusRef = useRef(false);

  const stableId = id || `async-select-${path.replace(/\W+/g, '-')}`;
  const shouldBypassCache = noCache || /\/(uoms|packages)/.test(path);

  // The custom option the current value names, if any. The endpoint holds no
  // row for it, so no fetch can ever resolve its label — every branch that
  // would otherwise wait on one has to check here first.
  const selectedExtra = useMemo(
    () => extraOptions.find((opt) => String(opt.value) === String(value)) ?? null,
    [extraOptions, value]
  );

  const toExtraOption = useCallback(
    (opt: { label: string; value: string }) => ({
      ...opt,
      origin: null,
      key: `${stableId}-${opt.value}-extra`,
    }),
    [stableId]
  );

  // Custom options matching the dropdown's current search text, in the shape
  // mapToOptions returns so the two lists concatenate cleanly.
  const extraOptionsFor = useCallback(
    (filter?: string) =>
      extraOptions
        .filter((opt) => !filter || opt.label.toLowerCase().includes(filter.toLowerCase()))
        .map(toExtraOption),
    [extraOptions, toExtraOption]
  );

  // Mark component as rendered after first paint so queries become enabled
  useEffect(() => {
    setIsComponentRendered(true);
  }, []);

  // Sync display value when initialSearchValue changes externally
  useEffect(() => {
    if (initialSearchValue && initialSearchValue !== searchQuery) {
      setSearchQuery(initialSearchValue);
      setDebouncedSearchQuery(initialSearchValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearchValue]);

  // Keep disabled state in sync with the displayed label
  useEffect(() => {
    if (!disabled) return;
    const labelToShow = initialSearchValue || value;
    if (labelToShow && searchQuery !== labelToShow) {
      setSearchQuery(labelToShow);
      setDebouncedSearchQuery(labelToShow);
    }
  }, [disabled, value, initialSearchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce search query; skip one cycle after a selection to avoid re-fetching with the selected label
  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // useLocalQuery always fetches the full list (empty query) for client-side filtering;
  // showFullList forces an empty query on focus when only the selected item is visible.
  const queryForFetch = showFullList || useLocalQuery ? '' : debouncedSearchQuery;

  // Defer the options-list fetch until the dropdown is actually opened. A field
  // that arrives with a prefilled value resolves its label via the cheap
  // default-value query below, so the full list only needs to load on focus.
  // Two cases keep the eager behavior: useLocalQuery (needs the whole list for
  // client-side filtering) and a value that has no other way to resolve its
  // label (no defaultValuePath and no initialSearchValue).
  const listEnabled =
    isComponentRendered &&
    !disabled &&
    !props.data &&
    (useLocalQuery || hasOpened || (!!value && !defaultValuePath && !initialSearchValue));

  // ── Options list query ──────────────────────────────────────────────────────
  const {
    data: optionsData,
    isFetching: isLoadingOptions,
    error: optionsError,
  } = useQuery({
    queryKey: ['async-select-options', path, queryForFetch, additionalParam],
    queryFn: async () => {
      const defaultParams = useDefault ? `offset=0&limit=10&${dynamicKeyParam}=${queryForFetch}&` : '';
      const queryString = additionalParam ? `${defaultParams}${additionalParam}` : defaultParams.slice(0, -1);
      const fullUrl = `${path}${queryString ? `?${queryString}` : ''}`;
      const response = await axiosInstance.get(fullUrl);
      return response.data;
    },
    staleTime: shouldBypassCache ? 0 : path.includes('get-all-hscode') ? 24 * 60 * 60 * 1000 : 12 * 60 * 60 * 1000,
    gcTime: shouldBypassCache ? 0 : 12 * 60 * 60 * 1000,
    enabled: listEnabled,
    throwOnError: false,
  });

  useEffect(() => {
    if (optionsError) {
      notifications.show({ title: 'Error', message: `Error when get ${label}`, color: 'red' });
    }
  }, [optionsError, label]);

  // ── Default value query ─────────────────────────────────────────────────────
  // Fetches the human-readable label for a pre-selected value.
  // Skipped when initialSearchValue already provides the label, or value looks like a label.
  const { data: defaultValueData, isFetching: isLoadingDefault } = useQuery({
    queryKey: ['async-select-default', defaultValuePath, value],
    queryFn: async () => {
      const response = await axiosInstance.get(`${defaultValuePath}${value}`);
      return response.data;
    },
    staleTime: 12 * 60 * 60 * 1000,
    gcTime: 12 * 60 * 60 * 1000,
    enabled:
      isComponentRendered &&
      !disabled &&
      !!value &&
      !!defaultValuePath &&
      !props.data &&
      !initialSearchValue &&
      !selectedExtra &&
      !(typeof value === 'string' && value.includes(' - ')),
    throwOnError: false,
  });

  // ── Option mapping helper ───────────────────────────────────────────────────
  const mapToOptions = useCallback(
    (data: any, localFilter?: string): any[] => {
      if (!data?.data) return [];
      const items: any[] = isArray(data.data) ? data.data : [data.data];
      if (!items.length) return [];

      const mapped = items.map((r: any, index: number) => {
        const labelOpt = optionsLabelKeys
          .map((k) => _.get(r, k))
          .filter((v) => v !== undefined)
          .join(' - ');
        return {
          label: renderOption ? renderOption(r) : labelOpt,
          value: _.get(r, valueOnResponseKey),
          origin: r,
          key: `${stableId}-${_.get(r, valueOnResponseKey)}-${index}`,
        };
      });

      const deduped = mapped.filter(
        (opt: any, idx: number, arr: any[]) =>
          arr.findIndex((o: any) => o.value === opt.value && o.label === opt.label) === idx
      );

      return localFilter !== undefined
        ? deduped.filter((opt: any) => opt.label.toLowerCase().includes(localFilter.toLowerCase()))
        : deduped;
    },
    [optionsLabelKeys, valueOnResponseKey, renderOption, stableId]
  );

  // ── Sync display label from defaultValueData ────────────────────────────────
  useEffect(() => {
    if (!defaultValueData || disabled) return;

    // Only update the visible text. Do NOT push the resolved label into
    // debouncedSearchQuery — that is part of the options-list query key and
    // would trigger a redundant list refetch keyed on the selected value. The
    // skip flag stops the debounce from propagating this display-only update.
    const applyDisplay = (text: string) => {
      setSearchQuery((prev) => {
        // Only arm the skip when the text actually changes. Arming on a no-op
        // update would leave the flag stuck (the debounce effect never re-runs
        // to clear it) and swallow the user's next real search keystroke.
        if (prev === text) return prev;
        skipNextSearchRef.current = true;
        return text;
      });
    };

    if (!defaultValueData.data) {
      applyDisplay(value);
      return;
    }

    const item = isArray(defaultValueData.data) ? defaultValueData.data[0] : defaultValueData.data;
    const labelOpt = optionsLabelKeys
      .map((k) => _.get(item, k))
      .filter((v) => v !== undefined)
      .join(' - ');

    if (labelOpt) {
      applyDisplay(labelOpt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValueData]);

  // ── Sync display label for a custom option ──────────────────────────────────
  // Its label comes from the prop, not from a fetch: the default-value query is
  // skipped for it above, so nothing else would ever fill the input.
  useEffect(() => {
    if (!selectedExtra || disabled) return;
    setSearchQuery((prev) => {
      // Same guard as the fetched-label effect: arming the skip on a no-op
      // would leave the flag stuck and swallow the next real keystroke.
      if (prev === selectedExtra.label) return prev;
      skipNextSearchRef.current = true;
      return selectedExtra.label;
    });
  }, [selectedExtra, disabled]);

  // ── Derived options list ────────────────────────────────────────────────────
  const options = useMemo(() => {
    // Static data prop bypasses all fetching
    if (props.data) return props.data;

    // Disabled: show a single static entry
    if (disabled) {
      const disabledLabel = initialSearchValue || value;
      return value || initialSearchValue
        ? [{ label: disabledLabel, value, origin: null, key: `${stableId}-${value || 'empty'}-disabled` }]
        : [];
    }

    // useLocalQuery: client-side filtering of the fully-fetched list
    if (useLocalQuery) {
      if (filterOptions) {
        const all = [...extraOptionsFor(), ...mapToOptions(optionsData)];
        // The input shows the selected label until the user types; that text
        // isn't a search, so open on the whole list, as the fetched mode does.
        const showsSelection = all.some((opt) => String(opt.value) === String(value) && opt.label === searchQuery);
        return filterOptions(all, showsSelection ? '' : searchQuery);
      }
      return [...extraOptionsFor(searchQuery), ...mapToOptions(optionsData, searchQuery)];
    }

    // Active search or showFullList: return results from the options query
    if (showFullList || debouncedSearchQuery) {
      return [...extraOptionsFor(debouncedSearchQuery), ...mapToOptions(optionsData)];
    }

    // No active search and the value is a custom option — it carries its own
    // label, and the two branches below can only resolve a fetched one.
    if (selectedExtra) {
      return [toExtraOption(selectedExtra)];
    }

    // No active search but a value is selected — prefer defaultValueData for the label
    if (defaultValueData?.data) {
      const item = isArray(defaultValueData.data) ? defaultValueData.data[0] : defaultValueData.data;
      const labelOpt = optionsLabelKeys
        .map((k) => _.get(item, k))
        .filter((v) => v !== undefined)
        .join(' - ');
      return [
        {
          label: labelOpt || value,
          value: _.get(item, valueOnResponseKey) || value,
          origin: item,
          key: `${stableId}-${_.get(item, valueOnResponseKey)}-default`,
        },
      ];
    }

    // Value present but no data yet: show a placeholder entry to avoid a blank input
    if (value) {
      const fallbackLabel = initialSearchValue || value;
      return [{ label: fallbackLabel, value, origin: null, key: `${stableId}-${value}-fallback` }];
    }

    return [...extraOptionsFor(), ...mapToOptions(optionsData)];
  }, [
    props.data,
    disabled,
    value,
    initialSearchValue,
    debouncedSearchQuery,
    optionsData,
    defaultValueData,
    optionsLabelKeys,
    valueOnResponseKey,
    stableId,
    useLocalQuery,
    searchQuery,
    mapToOptions,
    showFullList,
    selectedExtra,
    extraOptionsFor,
    toExtraOption,
    filterOptions,
  ]);

  const loading = isLoadingOptions || isLoadingDefault;

  useEffect(() => {
    if (!refocusRef.current) return;
    refocusRef.current = false;
    // Only a value that actually went empty. A parent that ignores '' leaves
    // the value alone, and a later change (a pick, auto-detection) must not
    // pull focus back here.
    if (!value) document.getElementById(stableId)?.focus();
  }, [value, stableId]);

  const getDisplayValue = () => searchQuery || initialSearchValue || '';

  const handleFocus = () => {
    if (disabled) return;
    // First focus enables the (previously deferred) options-list query.
    setHasOpened(true);
    if (useLocalQuery) return; // full list is always fetched for useLocalQuery
    // Prefetch the full list when the dropdown would otherwise open with only the selected item
    if (options.length <= 1 && !isLoadingOptions) {
      setShowFullList(true);
    }
  };

  return (
    <SearchableSelect
      key={`${stableId}-${value}`}
      id={stableId}
      name={name}
      label={label}
      required={required}
      onChange={(val) => {
        // Coerce both sides — API ids are often numbers while Combobox emits strings.
        if (!val) {
          // Emptied, not picked: the input remounts on the value change and
          // reads its text from here, so drop the old label now rather than
          // after the search debounce.
          setSearchQuery('');
          setDebouncedSearchQuery('');
          refocusRef.current = !!value && document.activeElement?.id === stableId;
          // A parent that ignores '' never changes the value; don't let the
          // flag outlive this event.
          window.setTimeout(() => {
            refocusRef.current = false;
          }, 0);
        }
        const selectedOption = options.find((r: any) => String(r.value) === String(val));
        if (selectedOption) {
          const labelText =
            typeof selectedOption.label === 'string' || typeof selectedOption.label === 'number'
              ? String(selectedOption.label)
              : String(selectedOption.value ?? val);
          // Prevent the debounce from treating the selected label as a new search query
          skipNextSearchRef.current = true;
          setSearchQuery(labelText);
          setDebouncedSearchQuery('');
          setShowFullList(false);
        }
        onChange(val, selectedOption);
      }}
      options={options}
      value={value}
      disabled={disabled}
      error={error}
      loading={loading}
      placeholder={placeholder}
      onSearchChange={(val) => {
        setSearchQuery(val);
        setShowFullList(false);
        if (!val) {
          onChange('', null);
        }
      }}
      initialSearchValue={getDisplayValue()}
      displayValue={getDisplayValue()}
      size={size}
      className={className}
      classNames={classNames}
      style={style}
      onFocus={handleFocus}
      onKeyPress={onKeyPress}
      onBlur={onBlur}
      clearable={clearable}
    />
  );
};

export default AsyncSelect;
